/**
 * Spanish (`es`) string catalogue — mirrors the key shape of `./en.ts`.
 * Brand names (`OVERVOLT`, `Sniper Rail`, `Arc Coil`, `Cryo Emitter`,
 * `Splash Mortar`, `Pulse Turret`, `Pylon`, `Flux`) stay in English; the
 * rest translates idiomatically into terse, gamey Spanish.
 */

import type { Catalogue } from './en';

export const es: Catalogue = {
  meta: {
    name: 'Español',
    code: 'ES',
  },

  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: 'PULSO',
      role: 'Generalista neutral. Daño fiable en cualquier punto del camino.',
    },
    arc: {
      label: 'Arc Coil',
      short: 'ARCO',
      role: 'Acoplado a la red. Más fuerte con superávit de Pylons, débil en apagón.',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: 'CRYO',
      role: 'Pintor de cuellos de botella. Radio corto — ralentiza y marca a cada enemigo en un punto.',
    },
    mortar: {
      label: 'Splash Mortar',
      short: 'MORT.',
      role: 'Fuego indirecto. Colócalo LEJOS del camino — su zona muerta es cercana.',
    },
    sniper: {
      label: 'Sniper Rail',
      short: 'SNIPER',
      role: 'Perforación en línea. Fuerte en rectas largas; débil en esquinas.',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: 'Suministro. Construye los suficientes para cubrir la demanda (y un superávit para Arc).',
    },
    wall: {
      label: 'Wall Node',
      short: 'MURO',
      role: 'Bloqueador barato — solo en modo Maze. Da forma a la ruta del enemigo.',
    },
  },

  roleHints: {
    pulse: 'Pulse Turret: tu opción fiable — daño sólido en cualquier punto del camino.',
    arc: 'Arc Coil: escala con el superávit de energía. Construye Pylons extra para una cadena más fuerte.',
    cryo: 'Cryo Emitter: pintura precisa del cuello. Ralentiza y marca las pocas casillas del punto de pinzamiento.',
    mortar: 'Splash Mortar: fuego indirecto. Colócalo LEJOS del camino — de cerca no acierta.',
    sniper: 'Sniper Rail: perforación en línea. Alinéalo con una recta larga para máximo efecto.',
    pylon: 'Power Pylon: alimenta la red. Construye lo suficiente para cubrir la demanda.',
    wall: 'Muro: bloqueador barato — da forma a la ruta que deben recorrer los enemigos.',
  },

  hints: {
    build:
      'Construye una torre: elige una abajo (o pulsa 1), luego haz clic en una casilla brillante junto al camino.',
    placed: 'Bien. Añade otra y pulsa Espacio para lanzar la oleada.',
    wave1: 'Las torres disparan solas. Mantén la barra de energía fuera del rojo.',
    select: 'Haz clic en una torre colocada para seleccionarla — U para mejorar, S para vender.',
    brownout: '¡Apagón! Torres ralentizadas. Construye un Pylon (3) para más energía.',
  },

  contextHints: {
    overload:
      'Overload: sube la próxima oleada para mayores recompensas — con más riesgo. ¿Arranque lento? Súbelo para más Flux.',
    profileKey: 'Pulsa P para abrir tu Perfil — el Flux desbloquea torres, habilidades y modos.',
    endlessMod: 'Endless rota MODIFICADORES de oleada — adapta tu composición.',
    puzzleHover:
      'Modo Puzzle: niveles lógicos hechos a mano con muros que bloquean la línea de visión. Un juego totalmente distinto.',
  },

  hud: {
    buildZone: 'CONSTRUIR',
    waveZone: 'OLEADA',
    abilitiesZone: 'HABILIDADES',
    overloadZone: 'OVERLOAD',

    startWave: 'LANZAR OLEADA',
    nextWave: 'SIG. OLEADA',
    pause: 'PAUSA',
    resume: 'CONTINUAR',
    restart: 'REINICIO',
    deployHint: 'lanzar [SPACE]',
    holdHint: 'pausa [SPACE]',
    rebootHint: 'reset [R]',
    nextWaveHint: 'SIG. OLEADA',

    audioOn: 'AUDIO ON',
    audioOff: 'AUDIO OFF',

    credits: 'CRÉDITOS',
    core: 'NÚCLEO',
    wave: 'OLEADA',
    endless: 'ENDLESS',
    score: 'PUNTOS',
    powerGrid: 'RED',

    runMode: 'MODO',
    difficulty: 'DIFICULTAD',
    profile: 'PERFIL',
    map: 'MAPA',
    clickHint: '▷ clic',

    locked: 'BLOQUEADO',
    lockedShort: 'BLOQ.',
    lockedTooltip:
      'BLOQUEADO — desbloquea por ✦{cost} Flux en el Perfil (pulsa P).',
    lockedTowerNotice: '{tower} bloqueado',
    lockedAbility: 'Habilidad bloqueada',
    lockedOverload: 'Nivel de Overload bloqueado',
    lockedMode: 'Modo {mode} bloqueado',
    lockedDifficulty: 'Hard bloqueado',

    cheatPip: '🔓 CHEAT',
    profilePip: '👤 {name}',

    overloadIdle: '— overload se aplica desde la oleada 1 —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ ACTIVO {sec}s',

    maxLevel: 'NIVEL MAX',
    upgrade: 'MEJORAR L{level}',
    sell: 'VENDER',
    target: 'OBJETIVO: {mode}',
    cycleTarget: 'cambiar · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: 'OVERCLOCK',
    overclockOn: 'OVERCLOCK ✦ ON',

    // Sprint 69 (i18n-gaps-extract-s69) — extracted from render.ts / hud.ts.
    buildHintIntro: "FASE DE CONSTRUCCIÓN  //  arma una torre [1·2·3·4·5], colócala y LANZA OLEADA [SPACE]",
    buildHintReinforce: "FASE DE CONSTRUCCIÓN  //  refuerza la red y LANZA OLEADA {wave} [SPACE]",
    mazeRoute: "R U T A   D E L   M A Z E",
    tiles: "CASILLAS",
    abilityActive: "◉ ACTIVO {sec}s",
    costSuffix: '{cost}c',
    overclockPip: 'OC ×{n}',
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    endlessModBadge: 'MOD · {label}',
    startWaveOverload: '⚡{n} {tierName}',
    waveOverloadPip: '⚡{n} {tierName}',
    overclockNoticeOn: "{tower} Overclock ACTIVADO",
    overclockNoticeOff: "{tower} Overclock DESACTIVADO",

    tierMult: {
      hp: "PV",
      sp: "VL",
      cn: "NM",
      gd: "OR",
      sc: "PT",
    },
    spawnShort: {
      bit: "Bit",
      packet: "Pkt",
      daemon: "Dmn",
      fork: "Fork",
      cipher: "Cph",
      patch: "Pch",
      buffer: "Buf",
      streak: "Stk",
      kernel: "KERNEL",
    },
  },

  modes: {
    campaign: 'CAMPAÑA',
    endless: 'ENDLESS',
    maze: 'MAZE',
    puzzle: 'PUZZLE',
  },
  modeSub: {
    campaign: '10 oleadas · ganar',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: 'elegir nivel',
  },
  modeBlurb: {
    campaign: '10 oleadas. Jefe en la oleada 10. Gana y te gradúas.',
    endless: 'Mismo mapa. Un modificador por oleada. Adáptate o filtra.',
    maze: 'Campo abierto. Trazas la ruta con muros. Ralentízalos o secas su botín.',
    puzzle: '10 niveles hechos a mano. Encuentra la única disposición ganadora.',
  },
  modeCard: {
    campaign: {
      tagline: 'Aprende la red, derrota al jefe.',
      bullet1: '10 oleadas afinadas + jefe',
      bullet2: 'Ruta S fija — construye, oleada, mejora',
      bullet3: 'Ganar desbloquea Endless en este mapa',
      length: '~8 min',
      skill: 'Trabaja el orden de construcción',
    },
    endless: {
      tagline: 'Adáptate o filtra. Sin final.',
      bullet1: 'Juega Campaña 1–10, luego escala sin fin',
      bullet2: 'Nuevo modificador cada oleada',
      bullet3: 'La partida acaba si el Núcleo cae',
      length: '~30 min',
      skill: 'Trabaja los giros largos',
    },
    maze: {
      tagline: 'Tú construyes el laberinto que recorren.',
      bullet1: 'Campo abierto — sin ruta dibujada',
      bullet2: 'Torres y muros baratos trazan la ruta',
      bullet3: 'Ruta más larga = más tiempo en fuego',
      length: '~12 min',
      skill: 'Trabaja el ruteo espacial',
    },
    puzzle: {
      tagline: 'Una disposición gana. Encuéntrala.',
      bullet1: '10 niveles, una sola solución',
      bullet2: 'Presupuesto y oleada fijos',
      bullet3: 'Muros LOS + límite de torres',
      length: '~2 min cada uno',
      skill: 'Trabaja la deducción',
    },
  },
  difficulty: {
    easy: 'FÁCIL',
    normal: 'NORMAL',
    hard: 'DIFÍCIL',
  },
  difficultySub: {
    easy: 'suave',
    normal: 'equilibrado',
    hard: '✦ {cost}',
  },

  overlay: {
    paused: 'PAUSA',
    pauseHint: 'PULSA  ESPACIO  PARA  CONTINUAR',

    missionComplete: '// MISIÓN CUMPLIDA',
    gridSecured: 'RED ASEGURADA',
    wavesRepelled: 'Las {total} oleadas de asalto han sido repelidas.',
    continueEndless: 'CONTINUAR  →  ENDLESS',
    unlockEndless: 'desbloquea Endless en el Perfil para continuar',
    restartCampaign: 'o pulsa  R  para reiniciar la campaña',

    systemFailure: '// FALLO DEL SISTEMA',
    coreBreached: 'NÚCLEO QUEBRADO',
    endlessEnded: 'Carrera Endless terminada en la oleada {wave}.',
    reactorOverrun: 'Reactor desbordado en la oleada {wave} de {total}.',
    runItBack: 'PULSA  R  PARA  REINTENTAR',

    finalScore: 'PUNTUACIÓN FINAL',
    newHighScore: '★  NUEVO RÉCORD  ★',
    newEndlessBest: '★  NUEVO RÉCORD ENDLESS  ★',
    highScore: 'RÉCORD',
    endlessBest: 'RÉCORD ENDLESS',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: "// RÉPLICA — DEUDA A LA RED  {sec}s //",
    overcharge: "// OVERCHARGE — RED REFORZADA  {sec}s //",
    brownout: "// CAÍDA DE RED — TORRES LIMITADAS //",
  },

  profile: {
    title: 'PERFIL',
    subtitle: '// META-PROGRESIÓN',
    closeHint: 'pulsa  P  o  ESC  para cerrar',
    tabUnlocks: 'DESBLOQUEOS',
    tabScoreboard: 'CLASIFICACIÓN',
    flux: 'FLUX',
    unlockTree: 'ÁRBOL DE DESBLOQUEO',
    unlockedCount: '{owned} / {total} DESBLOQUEADOS',
    lifetime: 'TOTAL',

    branchArsenal: 'ARSENAL',
    branchModes: 'MODOS',
    branchChallenge: 'DESAFÍO',
    branchCosmetic: 'COSMÉTICO',

    statRunsPlayed: 'PARTIDAS JUGADAS',
    statCampaignWins: 'VICTORIAS CAMPAÑA',
    statBestScore: 'MEJOR PUNTUACIÓN',
    statDeepestEndless: 'ENDLESS MÁS PROFUNDO',
    statTotalFlux: 'FLUX TOTAL',

    personalBests: 'RÉCORDS PERSONALES',
    waveLabel: 'OLEADA {wave}',

    runHistory: 'HISTORIAL',
    noRuns: 'Sin partidas aún — termina una y empieza la subida.',
    earlierRuns: '+ {n} partida anterior',
    earlierRunsPlural: '+ {n} partidas anteriores',
    colRun: 'PARTIDA',
    colMode: 'MODO · DIFICULTAD',
    colResult: 'RESULTADO',
    colOverload: 'OVL',
    colScore: 'PUNTOS',
    colDeltaBest: 'Δ MEJOR',
    colFlux: 'FLUX',
    starBest: '★ MEJOR',

    statusUnlocked: 'DESBLOQUEADO',
    statusOwned: 'POSEÍDO',
    statusRequiresPrereq: 'Antes {prereq}',
    statusFluxShort: 'Faltan ✦{short} Flux',
    statusAffordable: 'Asequible — clic para desbloquear',
    statusCost: '✦{cost} para desbloquear',
    buyAction: 'COMPRAR  ▸',
    nodeReq: 'REQ: {prereq}',

    profileLauncher: 'PERFIL',
    profileKeyHint: '[ P ]',
    viewScoreboard: 'VER  CLASIF.',
    newPersonalBest: '★  NUEVO RÉCORD PERSONAL  ★',
    fromYourBest: 'DESDE TU MEJOR',

    grantTower: 'TORRE DE ARMA',
    grantAbility: 'HABILIDAD ACTIVA',
    grantMode: 'MODO DE JUEGO',
    grantDifficulty: 'DIFICULTAD',
    grantOverload: 'NIVEL OVERLOAD {tier}',
    grantTheme: 'TEMA DE TABLERO',

    // Sprint 69 — unlock-tree hover preview.
    nodePreview: {
      towerMortar: "Fuego indirecto. Colócalo ATRÁS del camino — zona muerta cerca; obuses sobre clusters, rompe armadura.",
      towerSniper: "Lanza perforante en línea. Apunta una larga recta — todos en la línea reciben; débil en esquinas.",
      abilityAirstrike: "Explosión única en una casilla (45c). Limpia un cluster o astilla un grande.",
      abilityEmp: "Stun de campo (80c). Congela un instante a todos los enemigos si cae la línea.",
      modeEndless: "Juega más allá de la oleada 10 — oleadas procedurales hasta que caiga el Núcleo.",
      modeMaze: "Modo construcción en campo abierto. Las torres bloquean su casilla, A*-pathing alrededor.",
      difficultyHard: "Oleadas más duras; multiplicadores de oro y puntuación mayores.",
      overloadCritical: "Overload tier 3 — sube el calor para multiplicadores más gordos próxima oleada.",
      overloadMeltdown: "Overload tier 4 — la oleada predefinida más dura. Multiplicadores top.",
      themeAmber: "Paleta cosmética — terminal CRT amber cálido. Puramente cosmético.",
      themePhosphor: "Paleta cosmética — verde fósforo vintage. Puramente cosmético.",
    },
  },

  help: {
    eyebrow: '/ /  R E F E R E N C I A',
    title: 'MANUAL DE OPERACIONES',
    controls: 'CONTROLES',
    powerGrid: 'LA RED ELÉCTRICA',
    powerGridBody:
      'Las armas consumen energía; los Pylons la suministran. Cuando la demanda supera el suministro, la red entra en apagón y todas las torres se ralentizan — construye Pylons para mantenerlas disparando.',
    objective: 'OBJETIVO',
    objectiveBody:
      'Limpia las 15 oleadas de campaña para ganar; pierdes en el momento en que las vidas del Núcleo llegan a cero. El modo Endless dura para siempre.',
    mazeMode: 'MODO MAZE',
    mazeModeBody:
      'Un tercer modo en campo abierto: las torres bloquean su casilla y los enemigos las rodean con A*, así que tú construyes el laberinto. No puedes encerrar el Núcleo del todo.',
    glitches: 'GLITCHES',
    towers: 'TORRES',
    abilities: 'HABILIDADES',
    replayIntro: '↺  REPETIR INTRO',
    closeHint: 'Esc / H / ? o clic en cualquier sitio para cerrar',
  },

  intro: {
    eyebrow: '/ /  I N F O R M E   D E L   S I S T E M A',
    titleBig: 'OVERVOLT',
    subtitle: 'Defiende el Núcleo',
    line1:
      'Programas corruptos marchan por el circuito hacia tu Núcleo. Construye torres para destruirlos antes de que rompan la línea.',
    line2:
      '⚡ Cada torre consume energía. Si construyes demasiado, la red entra en apagón — todas las torres se ralentizan a la vez. Construye Pylons para mantener la energía.',
    line3: 'Construye en la fase de calma, luego pulsa Lanzar Oleada. Sobrevive a 15 oleadas para ganar.',
    line4: 'Ratón construye · 1–5 seleccionan · Espacio start/pausa · U mejorar · S vender',
    gotIt: 'ENTENDIDO  →',
    gotItHint: 'pulsa cualquier tecla para continuar',
  },

  puzzle: {
    label: 'PUZZLE',
    levels: {
      'p0-first-light': {
        name: 'Primera Luz',
        hint: 'Coloca un Pulse junto al corredor. Cualquier casilla a tiro vale.',
      },
      'p1-long-lance': {
        name: 'Lanza Larga',
        hint: 'Un Sniper alineado con el carril perfora a cada enemigo.',
      },
      'p2-over-the-wall': {
        name: 'Sobre el Muro',
        hint: 'El muro bloquea todo haz directo. Los obuses de mortero pasan en parábola.',
      },
      'p3-funhouse-mirror': {
        name: 'Espejo Deformante',
        hint: 'Un Sniper enhebrando la rendija acierta en ambos carriles.',
      },
      'p4-surplus-riches': {
        name: 'Riquezas en Exceso',
        hint: 'Construye Pylons más allá de la banda libre. El Arc Coil prospera con el superávit.',
      },
      'p5-pinch-point': {
        name: 'Punto de Pinzamiento',
        hint: 'El Cryo pinta el cuello; los Pulses alrededor liquidan al grupo.',
      },
      'p6-cipher-gauntlet': {
        name: 'Guantelete Cipher',
        hint: 'El mortero destroza armadura; el Cryo pinta la curva; el Sniper perfora la pierna inferior. Usa todo el equipo.',
      },
      'p7-kernel-echo': {
        name: 'Eco del Kernel',
        hint: 'Los morteros lanzan por encima de los muros de LOS. El Sniper enhebra desde abajo. Vigila el Power Drain.',
      },
      'p8-overclock-crunch': {
        name: 'Crisis Overclock',
        hint: 'Un Pulse no basta. Overclock en la ventana clave — la cadencia sostenida no llega.',
      },
      'p9-pylon-pinch': {
        name: 'Pinzamiento de Pylons',
        hint: 'Agrupa Pylons entre tus torres — el tope de aura premia una zona apretada.',
      },
    },
    briefing: {
      eyebrow: "BRIEFING PUZZLE",
      startBtn: "Iniciar ▷",
      cancelBtn: "Cancelar",
      sectionConstraints: "RESTRICCIONES",
      sectionTowers: "TORRES",
      sectionWinCondition: "CONDICIÓN DE VICTORIA",
      budgetLabel: "Presupuesto de oro",
      wavesLabel: "Oleadas",
      towerAllowed: "PERMITIDA",
      towerForbidden: "BLOQUEADA",
      resultEyebrowWon: "PUZZLE COMPLETO",
      resultEyebrowLost: "PUZZLE FALLIDO",
      resultTitleWon: "¡RESUELTO!",
      resultTitleLost: "INTÉNTALO DE NUEVO",
      resultStatTowers: "TORRES",
      resultStatGold: "ORO GASTADO",
      resultStatLives: "VIDAS RESTANTES",
      resultNextBtn: "Siguiente nivel ▷",
      resultReplayBtn: "Repetir",
      resultResetBtn: "Reiniciar intento",
      resultBackBtn: "Volver a selección",
    },

    // Sprint 69 — puzzle level-select overlay chrome.
    select: {
      subtitle: "Niveles lógicos hechos a mano — deduce la única disposición ganadora.",
      close: "Cerrar",
      cardTitle: 'P{index}  {name}',
      lockedCard: "🔒 BLOQUEADO",
      solved: "✓ Resuelto",
      unsolved: "▷ Sin resolver",
      noSolve: "Sin resolver aún",
      bestLine: "Mejor: {towers}t · {gold}g · {power}p",
      allowed: "Permitido: {list}",
      parPlaceholder: "par: — / — / —",
      parRow: "{towers}t · {gold}g · {power}p",
    },
  },

  localeChip:
 {
    label: 'IDIOMA',
    en: 'EN',
    cs: 'CS',
  },

  resume: {
    title: '¿Reanudar la partida anterior?',
    resume: 'Reanudar',
    newRun: 'Nueva',
    hint: '[Enter] Reanudar   ·   [Esc] Nueva',
    detail: "{mode} · {difficulty} · oleada {wave} · {towers} torres · {gold}g · {lives}♥",
  },

  // -------- Sprint 69: Kernel boss UI chrome ---------------------------------
  boss: {
    kernelHeader: "⚠  EL KERNEL",
    phase: "FASE {phase} · {name}",
    powerDrain: "⚡ DRENAJE DE ENERGÍA",
    kernelIncoming: "⚠  KERNEL  ENTRANTE  ⚠",
    phaseName: {
      boot: "BOOT SEQUENCE",
      fork: "FORK BOMB",
      panic: "KERNEL PANIC",
    },
  },

  // -------- Sprint 69: Map registry display strings --------------------------
  maps: {
    default: { label: "Datawash", tagline: "Curva en S abierta — el clásico OVERVOLT." },
    cramped: { label: "Switchyard", tagline: "Pasillos estrechos — cada casilla cuenta." },
    lines: { label: "Long Lines", tagline: "Largas rectas — guía la lanza." },
    serpentine: { label: "Snake Run", tagline: "Serpentina cerrada — el Cryo painter brilla." },
  },

  // -------- Sprint 69: Ability display names ---------------------------------
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: Overload tier names -----------------------------------
  overload: {
    tierName: {
      nominal: "Nominal",
      charged: "Cargado",
      surging: "Crecido",
      critical: "Crítico",
      meltdown: "Meltdown",
    },
  },

  // -------- Sprint 69: Endless modifier labels -------------------------------
  endlessMod: {
    armoredSurge: { label: "Carga Blindada" },
    fastPack: { label: "Manada Rápida" },
    scatteredSpread: { label: "Dispersión" },
    streakRunners: { label: "Corredores Streak" },
    daemonRush: { label: "Embestida Daemon" },
  },

  // -------- Sprint 69: Enemy display names (Help roster) ---------------------
  enemies: {
    bit: { label: "Bit" },
    packet: { label: "Packet" },
    daemon: { label: "Daemon" },
    fork: { label: "Fork" },
    cipher: { label: "Cipher" },
    patch: { label: "Patch" },
    buffer: { label: "Buffer" },
    streak: { label: "Streak" },
    kernel: { label: "Kernel" },
  },

  // -------- Sprint 69: Help-panel enemy one-liners ---------------------------
  enemyRole: {
    bit: "Carne de cañón — barato, lento, frágil.",
    packet: "Rápido y débil; corre hacia el Núcleo.",
    daemon: "Esponja de HP; golpea fuerte si filtra.",
    fork: "Se divide en dos Bits al morir.",
    cipher: "Blindado — destrózalo con Corrosión del Mortero.",
    patch: "Cura Glitches cercanos; bájalo primero.",
    buffer: "Escudo regenerativo; rómpelo con burst.",
    streak: "Esprinta solo; resiste slow. Cubre el camino.",
    kernel: "Boss por fases — drena tu energía. Burstéalo antes de la siguiente fase.",
  },

  // -------- Sprint 69: Help-panel ability one-liners -------------------------
  abilityRole: {
    overcharge: "Empuja la red: cada torre dispara más rápido un instante. Planéalo para una oleada dura.",
    airstrike: "Explosión única en una casilla. Limpia un cluster o astilla un grande.",
    empBurst: "Stun de campo — todos los enemigos congelan un instante. Compra tiempo si cae la línea.",
  },

  // -------- Sprint 69: Tooltip chrome ----------------------------------------
  tooltip: {
    levelOf: "LV {level}/{max}",
    credits: "{cost} créditos",
    next: "SIGUIENTE  →  LV {level}   {cost} créditos",
    cooldown: "COOLDOWN  {sec}s",
    stat: {
      supply: "SUMINISTRO  +{n}w a la red",
      dmgRngCd: "DMG {damage}    RNG {range}    CD {cd}s",
      pwrChain: "PWR {power}w    CADENA {chain}",
      pwrAoe: "PWR {power}w    AOE {targets} objetivos",
      slow: "SLOW ×{factor}  por  {sec}s",
      brittle: "FRÁGIL +{pct}% dmg de otras torres",
      pwrBlast: "PWR {power}w    BLAST {tiles} casillas",
      pwrSingle: "PWR {power}w    1 objetivo",
    },
  },

  // -------- Sprint 69: Help-panel control-row labels -------------------------
  controls: {
    arm: "Arma Pulse / Arc / Pylon / Cryo / Mortero / Sniper",
    mouse: "Coloca en casilla · clic en torre para seleccionar",
    space: "Lanza oleada · pausa oleada",
    fast: "Avance rápido (1× ⇄ 2×)",
    upgradeSell: "Mejora / vende la torre seleccionada",
    target: "Cambia el modo de objetivo de la torre seleccionada",
    profile: "Abre Perfil (árbol · marcador)",
    utility: "Cancela · silencia · reinicia la partida",
  },

  // -------- Sprint 69: Unlock-tree node display names ------------------------
  unlocks: {
    towerMortar: { name: "Splash Mortar" },
    towerSniper: { name: "Sniper Rail" },
    abilityAirstrike: { name: "Airstrike" },
    abilityEmp: { name: "EMP Burst" },
    modeEndless: { name: "Modo Endless" },
    modeMaze: { name: "Modo Maze" },
    difficultyHard: { name: "Dificultad Difícil" },
    overloadCritical: { name: "Overload: Crítico" },
    overloadMeltdown: { name: "Overload: Meltdown" },
    themeAmber: { name: "Terminal Amber" },
    themePhosphor: { name: "Verde Fósforo" },
  },

  // Sprint 73 — tutorial interactivo (docs/TUTORIAL_DESIGN.md §6).
  tutorial: {
    button: {
      start: 'INICIAR TUTORIAL',
      startSub: '¿Primera vez? Haz el tour →',
      replay: 'REPETIR TOUR',
      replaySub: 'Refrescar lo básico',
    },
    step: {
      welcome: {
        title: 'Bienvenido a OVERVOLT',
        body: 'Eres la IA defensiva de un reactor que falla. Programas corruptos marchan por el circuito hacia tu Core — destrúyelos antes de que rompan.',
      },
      path: {
        title: 'La ruta',
        body: 'Los enemigos siempre siguen esta curva en S, desde la casilla spawn a la izquierda hasta el Core a la derecha. Tú decides dónde plantar torres.',
      },
      firstPlace: {
        title: 'Coloca tu primera torre',
        body: 'Una Pulse Turret cuesta 50 créditos y consume 4 de potencia. Haz clic en la casilla brillante junto a la ruta.',
      },
      wave1: {
        title: 'Lanza la primera oleada',
        body: 'Pulsa INICIAR OLEADA. Tu Pulse dispara automáticamente a lo que esté al alcance.',
      },
      towerRoles: {
        title: 'Cinco torres, cinco roles',
        body: 'Pulse: fiable en todas partes. Sniper: rectas largas. Mortar: fuego indirecto, ATRÁS de la ruta. Arc: escala con superávit. Cryo: pinta un cuello de botella.',
      },
      powerGrid: {
        title: 'La red eléctrica',
        body: 'Cada torre consume. Cuando la demanda supera el suministro, la red sufre brownout y todas se ralentizan. Construye Pylons — vecinos suben la cadencia.',
      },
      overloadDial: {
        title: 'Overload — riesgo vs recompensa',
        body: 'Sube el dial para la siguiente oleada: enemigos más duros, más Flux. Bájalo para jugar seguro.',
      },
      overclock: {
        title: 'Overclockea una torre',
        body: 'Activa OVERCLOCK en una torre seleccionada para una ventana burst: disparo más rápido, mayor consumo. Ganancia burst, pérdida sostenida.',
      },
      abilities: {
        title: 'Habilidades activas',
        body: 'Tres ranuras: Overcharge potencia toda la red. Airstrike deja una zona quemada duradera. EMP Burst aturde a todos. Planifica — no pulses por pánico.',
      },
      mazePreview: {
        title: 'Modo Maze',
        body: 'Sin ruta fija — las torres bloquean su casilla y los enemigos A*-rutan alrededor. Tú construyes el camino que deben recorrer.',
      },
      puzzlePreview: {
        title: 'Modo Puzzle',
        body: 'Niveles lógicos hechos a mano con muros de línea de visión. Presupuesto fijo, oleada fija — encuentra la única disposición ganadora. Juego distinto.',
      },
      wrapUp: {
        title: 'Estás listo',
        body: 'Las partidas dan FLUX — gástalo en el Perfil para desbloquear torres, habilidades y modos. Empieza por Campaign; vuelve para Endless, Maze y Puzzle.',
      },
    },
    ui: {
      eyebrow: 'PASO {n} / {total}',
      next: 'SIGUIENTE →',
      back: '← ATRÁS',
      skip: 'SALTAR TOUR',
      play: 'JUGAR  ▸',
      skipConfirm: '¿Saltar el tour? Puedes repetirlo cuando quieras desde la pantalla de título.',
    },
  },
};
